import cn from 'classnames';
import React from 'react';

import { Caption, H3 } from 'components/basic/Typography';
import { CircularProgress } from 'components/basic/Loaders';
import { FormattedPrice } from 'components/domains/payments/FormattedPrice';

import s from './PaymentForm.module.scss';

interface Props {
  status: 'idle' | 'loading' | 'error' | 'ready';
  subtotal: string | null;
  tax: string | null;
  total: string | null;
  currency?: string;
  error?: string;
}

export default function PaymentPreviewConfirm({
  status,
  subtotal,
  tax,
  total,
  currency,
  error,
}: Props): JSX.Element {
  const isLoading = status === 'loading';
  const isError = status === 'error';

  if (status === 'idle') {
    return <div />;
  }

  if (isLoading) {
    return (
      <div className={s.paymentPreviewLoadingWrapper}>
        <CircularProgress className={s.paymentPreviewLoading} />
        <Caption className="ml-2">Calculating tax…</Caption>
      </div>
    )
  }

  return (
    <div className={cn(s.previewCard, 'mt-6')}>
      <H3 className={cn(s.previewTitle, 'mb-3')}>Review total</H3>

      {isError && (
        <Caption className={cn(s.previewError, 'mb-3')}>
          {error || 'Failed to load tax preview. Try again.'}
        </Caption>
      )}

      {!isLoading && !isError && (
        <>
          <div className={s.previewRow}>
            <Caption>Subtotal</Caption>
            <Caption>
              {subtotal !== null ? (
                <FormattedPrice price={subtotal} currency={currency || 'usd'} />
              ) : (
                '-'
              )}
            </Caption>
          </div>
          {tax !== null && (
            <div className={s.previewRow}>
              <Caption>Tax</Caption>
              <Caption>
                <FormattedPrice price={tax} currency={currency || 'usd'} />
              </Caption>
            </div>
          )}
          <div className={s.previewRowTotal}>
            <Caption>Total</Caption>
            <Caption>
              {total !== null ? (
                <FormattedPrice price={total} currency={currency || 'usd'} />
              ) : (
                '-'
              )}
            </Caption>
          </div>
        </>
      )}
    </div>
  );
}
